import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { RoleType } from "../../models/Role";
import { DocumentOfReceive } from "../../models/DocumentOfReceive";
import { activeUser } from "../../services/AuthService";
import { updateOneDocument } from "../../services/NewVersionService";
import "./InvoiceIdForm.scss";

export interface NewInvoiceIdFormProps {
  show?: string;
  selectedDocument?: DocumentOfReceive;
  onSaved?: () => void;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewInvoiceIdForm = (p: NewInvoiceIdFormProps) => {
  const {
    selectedDocument,
    show,
    onSaved,
    buttonStyle,
    buttonSaveStyle,
    buttonResetStyle,
  } = p;

  const { t } = useTranslation();

  const [visibleNewDocumentForm, setVisibleNewDocumentForm] =
    useState<boolean>(false);
  const [newDocument, setNewDocument] = useState<DocumentOfReceive>();

  const toast = useRef(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  useEffect(() => {
    if (show) {
      setVisibleNewDocumentForm(true);
    }
  }, [show]);

  useEffect(() => {
    if (selectedDocument) {
      setNewDocument(selectedDocument);
    }
  }, [selectedDocument]);

  const showModal = () => {
    setVisibleNewDocumentForm(!visibleNewDocumentForm ? true : false);
  };

  const handleNewDocumentForm = (prop: string, value: any) => {
    setNewDocument((newDocument: any) => ({
      ...newDocument,
      [prop]: value,
    }));
  };

  const onSubmit = async () => {
    const doc = newDocument as DocumentOfReceive | undefined;
    if (!doc?.id) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t("Nedostaje ID dokumenta"),
        life: 3000,
      });
      return;
    }

    try {
      await updateOneDocument(doc.id, {
        shipperInvoiceId: doc.shipperInvoiceId ?? "",
        controlHouseInvoiceId: doc.controlHouseInvoiceId ?? "",
      });

      handleResetModal();
      onSaved?.();

      //@ts-ignore
      toast.current.show({
        severity: "success",
        detail: t("Uspešno izmenjeni podaci"),
        life: 3000,
      });
    } catch (e) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t("Greška pri snimanju"),
        life: 3000,
      });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    let copyOfNewDocument = cloneDeep(newDocument);
    if (copyOfNewDocument) {
      copyOfNewDocument.shipperInvoiceId = "";
      copyOfNewDocument.controlHouseInvoiceId = "";
      setNewDocument(copyOfNewDocument);
    }
    showModal();
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-file-text" style={{ fontSize: 18 }}></i>
      </span>
      <span>
        {t("Unošenje broja računa transporta i broja računa kontrolne kuće")}
      </span>
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Potvrdi")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={
          !(
            activeUser.role?.name === RoleType.Superadmin ||
            activeUser.role?.name === RoleType.Administrator ||
            activeUser.role?.name === RoleType.Backoffice
          )
        }
      />
      <ButtonInput
        labelText={t("Resetuj")}
        onButtonClick={handleResetModal}
        addStyle={addButtonResetStyle}
        disabled={
          !(
            activeUser.role?.name === RoleType.Superadmin ||
            activeUser.role?.name === RoleType.Administrator ||
            activeUser.role?.name === RoleType.Backoffice
          )
        }
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} position={"top-right"} />
      {visibleNewDocumentForm ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={showModal}
          >
            <div className="new-invoice-id-form-body">
              <TextInputWithIcon
                id="shipperInvoiceId"
                value={newDocument?.shipperInvoiceId as string}
                onValueChange={handleNewDocumentForm}
                labelText={`${t("Broj računa transporta")}`}
                icon="bi bi-file-text"
                iconAlign="left"
                placeholderText={t("Unesite broj računa transporta")}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator ||
                    activeUser.role?.name === RoleType.Backoffice
                  )
                }
              />
              <TextInputWithIcon
                id="controlHouseInvoiceId"
                value={newDocument?.controlHouseInvoiceId as string}
                onValueChange={handleNewDocumentForm}
                labelText={`${t("Broj računa kontrolne kuće")}`}
                icon="bi bi-file-text"
                iconAlign="left"
                placeholderText={t("Unesite broj računa kontrolne kuće")}
                control={control}
                errors={errors}
                setValue={setValue}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator ||
                    activeUser.role?.name === RoleType.Backoffice
                  )
                }
              />
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
